import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/cn';

/*
 * Tooltip - kontekstuaalinen vihje Radix UI Tooltipin paalla.
 *
 * Radix tuo:
 *  - hover/focus-aktivoinnin (touch-laitteilla long-press)
 *  - keyboard-tuen (Esc sulkee)
 *  - portal-renderoinnin (ei taistele z-indexien kanssa)
 *  - aria-attribuutit
 *
 * TooltipProvider tarvitaan juuressa (yleensa AppLayoutissa). Yksi provider per app.
 * Esimerkki:
 *
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button variant="ghost"><Info /></Button>
 *     </TooltipTrigger>
 *     <TooltipContent>
 *       Tama on AI-generoitu sisalto, tarkista ennen julkaisua.
 *     </TooltipContent>
 *   </Tooltip>
 *
 * Mockup-vastine: monessa nakymassa info-glyphi (SEED 12.13: tooltipit
 * MasterForge-tyyliin kriittisissa kohdissa, esim. mittareiden selitykset
 * arkikielella SEED 12.16:n SEURAA-moduulissa).
 */

export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(function TooltipContent({ className, sideOffset = 4, ...props }, ref) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 max-w-xs rounded-sm bg-yo px-3 py-1.5 text-sm text-white shadow-card-hover',
          'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
